import {useCallback, useRef, useState} from "react";
import type {PointerEvent as ReactPointerEvent} from "react";
import cn from "classnames";
import "./index.css";
import {
  CODE_HIGHLIGHT_OUTLINE_PX,
  CODE_HIGHLIGHT_RADIUS_PX,
  RESIZE_HANDLES,
  codeHighlightFill,
  moveCodeHighlightBox,
  resizeCodeHighlightBox,
} from "libs/codeHighlight";
import type {CodeHighlight as CodeHighlightValue, CodeHighlightBox, ResizeHandle} from "libs/codeHighlight";

type Props = {
  highlight: CodeHighlightValue;
  /** The banner size: the box is in these pixels. */
  canvas: {width: number; height: number};
  /** Omit the writer to display a fixed highlight without editing controls. */
  onChange?: (next: CodeHighlightValue) => void;
};

type Drag = {
  handle: ResizeHandle | null;
  startX: number;
  startY: number;
  start: CodeHighlightBox;
  /** Banner pixels per screen pixel. */
  scale: number;
};

/**
 * The code highlight over a screenshot (libs/codeHighlight.ts). Drag the box
 * to move it; drag a handle to change the width, the height, or both. The
 * outline and the corner radius do not scale with the box.
 *
 * It takes the pointer also while the screenshot or background tools are
 * open: an upload opens the image tools, and the highlight then had to be
 * placed on the new screenshot (2026-10-31). A drag elsewhere still pans the
 * image.
 *
 * Put it in the banner's first child, above the screenshot: the export draws
 * it. The handles are editor chrome and render mode hides them.
 */
const CodeHighlight = ({highlight, canvas, onChange}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);
  const [dragging, setDragging] = useState(false);

  const begin = useCallback(
    (event: ReactPointerEvent<HTMLElement>, handle: ResizeHandle | null) => {
      if (event.button !== 0) return;
      const banner = ref.current?.parentElement;
      if (!banner) throw new Error("CodeHighlight must be inside the banner");
      event.preventDefault();
      event.stopPropagation();
      // The preview is the banner at a zoom: measure it, so a drag of one
      // screen pixel is that many banner pixels at any zoom.
      const scale = canvas.width / banner.getBoundingClientRect().width;
      const {x, y, width, height} = highlight;
      drag.current = {handle, startX: event.clientX, startY: event.clientY, start: {x, y, width, height}, scale};
      ref.current?.setPointerCapture(event.pointerId);
      setDragging(true);
    },
    [canvas.width, highlight]
  );

  const move = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const current = drag.current;
      if (!current || !onChange) return;
      event.preventDefault();
      const dx = (event.clientX - current.startX) * current.scale;
      const dy = (event.clientY - current.startY) * current.scale;
      const box = current.handle
        ? resizeCodeHighlightBox(current.start, current.handle, dx, dy, canvas)
        : moveCodeHighlightBox(current.start, dx, dy, canvas);
      if (
        box.x !== highlight.x ||
        box.y !== highlight.y ||
        box.width !== highlight.width ||
        box.height !== highlight.height
      ) {
        onChange({...highlight, ...box});
      }
    },
    [canvas, highlight, onChange]
  );

  const end = useCallback(() => {
    drag.current = null;
    setDragging(false);
  }, []);

  if (!highlight.enabled) return null;

  return (
    <div
      ref={ref}
      className={cn("code-highlight", {dragging, "code-highlight--readonly": !onChange})}
      data-code-highlight=""
      style={{
        left: highlight.x,
        top: highlight.y,
        width: highlight.width,
        height: highlight.height,
        borderRadius: CODE_HIGHLIGHT_RADIUS_PX,
        backgroundColor: codeHighlightFill(highlight.color),
        // Figma's outline is outside the box; a spread shadow is too, and its
        // corners follow the radius (10 + 4 px outside).
        boxShadow: `0 0 0 ${CODE_HIGHLIGHT_OUTLINE_PX}px ${highlight.color}`,
      }}
      onPointerDown={onChange ? (event) => begin(event, null) : undefined}
      onPointerMove={onChange ? move : undefined}
      onPointerUp={onChange ? end : undefined}
      onPointerCancel={onChange ? end : undefined}
    >
      {onChange && RESIZE_HANDLES.map((handle) => (
        <span
          key={handle}
          className={cn("code-highlight__handle", `code-highlight__handle--${handle}`)}
          onPointerDown={(event) => begin(event, handle)}
        />
      ))}
    </div>
  );
};

export default CodeHighlight;
